import classNames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import type { NavigationViewModel } from '~/Features/Navigation/NavigationViewModel.csharp';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import { List } from '~/Partials/List/List';
import { BasketButton } from '~/Partials/Nav/Components/BasketButton/BasketButton';
import { BasketStateObserver } from '~/Partials/Nav/Components/BasketButton/BasketStateObserver';
import type { MenuContentButtonProps } from '~/Partials/Nav/Components/MenuContentButton/MenuContentButton';
import { MenuContentButton } from '~/Partials/Nav/Components/MenuContentButton/MenuContentButton';
import { MyPageButton } from '~/Partials/Nav/Components/MyPageButton/MyPageButton';
import type { NavButtonVariant } from '~/Partials/Nav/Components/NavButtons/NavButtons';
import { NavButtons } from '~/Partials/Nav/Components/NavButtons/NavButtons';
import { SearchButton } from '~/Partials/Nav/Components/SearchButton/SearchButton';
import { NavItem } from '~/Partials/Nav/NavItem';
import { ScrollLock } from '~/Utils/ScrollLock/ScrollLock';
import navButtonStyles from './Components/Shared/Styles/NavButton.module.css';
import styles from './Nav.module.css';

interface NavState {
    isFirstPaint: boolean;
    isApplicationInProgress: boolean;
    basketLinkUrl: string;
    openedItem?: NavItem;
}

interface NavProps {
    navElementsColor: NavButtonVariant;
    changeSearchOpened: (searchStatus: boolean) => void;
    model: Omit<NavigationViewModel, 'menuColumnsCount'> & {
        menuColumnsLayout: Pick<MenuContentButtonProps, 'menuContentProps'>['menuContentProps']['menuColumnsLayout'];
    };
}

export const Nav: React.FC<React.PropsWithChildren<NavProps>> = props => {
    const { model, navElementsColor, changeSearchOpened } = props;
    const navContainerRef = useRef<HTMLUListElement>(null);
    const [navState, setNavState] = useState<NavState>({
        openedItem: undefined,
        isFirstPaint: true,
        isApplicationInProgress: false,
        basketLinkUrl: '',
    });

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                navContainerRef.current &&
                event.target instanceof Node &&
                !navContainerRef.current.contains(event.target)
            ) {
                changeSearchOpened(false);
                setNavState(prevState => ({ ...prevState, openedItem: undefined }));
            }
        }

        document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const isOpened = (item: NavItem) => navState.openedItem === item;

    const toggleItem = (item: NavItem) => {
        changeSearchOpened(item === NavItem.Search && !isOpened(NavItem.Search));
        setNavState(prevState => ({
            ...prevState,
            openedItem: prevState.openedItem === item ? undefined : item,
            isFirstPaint: false,
        }));
    };

    const { navigationLinks, translations, languages } = model;
    const { isFirstPaint, isApplicationInProgress, basketLinkUrl } = navState;

    return (
        <>
            <ScrollLock isLocked={isOpened(NavItem.Menu)} />
            <nav
                ref={navContainerRef}
                className={classNames(
                    styles.Nav,
                    !isFirstPaint && isOpened(NavItem.Search) && styles['Variant--searchOpen']
                )}
            >
                <List
                    items={navigationLinks}
                    renderItem={item => (
                        <li className={styles.ListItem} key={`LinkButton__${item.id}`}>
                            <Link
                                {...item.link}
                                type={LinkType.Clear}
                                className={classNames(
                                    navButtonStyles.Button,
                                    navButtonStyles[`Variant--${navElementsColor}`]
                                )}
                            />
                        </li>
                    )}
                    className={styles.LinkList}
                />
                <BasketStateObserver
                    onStateChanged={(inInProgress, linkUrl) =>
                        setNavState(prevState => ({
                            ...prevState,
                            isApplicationInProgress: inInProgress,
                            basketLinkUrl: linkUrl,
                        }))
                    }
                    {...model}
                />
                <NavButtons>
                    {isApplicationInProgress && (
                        <BasketButton
                            buttonKey="BasketButton"
                            basketLinkUrl={basketLinkUrl}
                            translations={translations}
                            variant={navElementsColor}
                        />
                    )}
                    <MyPageButton
                        buttonKey="MyPageButton"
                        {...model}
                        variant={navElementsColor}
                        showMyPageTranslation={translations.showMyPage}
                    />
                    <SearchButton
                        buttonKey="SearchButton"
                        {...model}
                        translations={{ ...translations }}
                        isFirstPaint={isFirstPaint}
                        isOpened={isOpened}
                        toggleItem={toggleItem}
                        variant={navElementsColor}
                    />
                    <MenuContentButton
                        buttonKey="MenuContentButton"
                        {...model}
                        variant={navElementsColor}
                        toggleItem={toggleItem}
                        isOpened={isOpened}
                        isFirstPaint={isFirstPaint}
                        menuTranslation={translations.menu}
                        menuContentProps={{
                            ...model,
                            languages,
                            translations,
                        }}
                    />
                </NavButtons>
            </nav>
        </>
    );
};
